import {
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fade,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import LoadingButton from "../../../components/ui/LoadingButton";
import IconButton from "../../../components/ui/IconButton";
import { createFilterOptions } from "@mui/material/Autocomplete";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import AddIcon from "@mui/icons-material/Add";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { useCountry } from "../../../contexts/CountryContext";
import { useIntl } from "../../../contexts/IntlContext";
import { AuthContext } from "../../../contexts/AuthContext";
import { isAuthOwner } from "../../../utils/authRole";
import api from "../../../services/api";
import { fetchEstablishmentsForFilters } from "../../../services/establishmentsResolver";
import ConfirmDialog from "../../../components/ConfirmDialog";
import { TableEmptyState } from "../../../components/table/TableEmptyState";
import { TableSkeletonRows } from "../../../components/table/TableSkeletonRows";
import Autocomplete from "../../../components/ui/Autocomplete";
import Input from "../../../components/ui/Input";

type LivePlaylistAdmin = {
  id: number;
  name: string;
  youtubeId: string;
  youtubeAccountId?: number | null;
  youtubeAccountName?: string | null;
};

type PlaylistDraft = { id?: number; name: string; youtubeId: string; youtubeAccountId: number | "" };
type EstablishmentOption = { id: string; name: string; livePlaylistId?: number | null };
type YoutubeAccountOption = { id: number; name: string };

const establishmentFilter = createFilterOptions<EstablishmentOption>({
  stringify: (opt) => `${opt.id} ${opt.name}`,
});
const youtubeAccountFilter = createFilterOptions<YoutubeAccountOption>({
  stringify: (opt) => `${opt.id} ${opt.name}`,
});

type PlaylistsManagerProps = {
  youtubeBlocked?: boolean;
};

export default function PlaylistsManager({ youtubeBlocked = false }: PlaylistsManagerProps) {
  const intl = useIntl();
  const { country } = useCountry();
  const { authInfo } = useContext(AuthContext);
  const isOwner = isAuthOwner(authInfo?.role);

  const [rows, setRows] = useState<LivePlaylistAdmin[]>([]);
  const [loading, setLoading] = useState(false);
  const [establishments, setEstablishments] = useState<EstablishmentOption[]>([]);
  const [loadingEstablishments, setLoadingEstablishments] = useState(false);
  const [youtubeAccounts, setYoutubeAccounts] = useState<YoutubeAccountOption[]>([]);
  const [filterYoutubeAccountId, setFilterYoutubeAccountId] = useState<number | "">("");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [draft, setDraft] = useState<PlaylistDraft>({ name: "", youtubeId: "", youtubeAccountId: "" });
  const [draftEstablishmentId, setDraftEstablishmentId] = useState<string>("");
  const [nameTouched, setNameTouched] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<LivePlaylistAdmin | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      try {
        if (isOwner) throw new Error("skip admin endpoint for owner");
        const response = await api.get<{ data: LivePlaylistAdmin[] }>("/app/lives/playlists/admin", {
          ...(filterYoutubeAccountId !== "" ? { params: { youtubeAccountId: filterYoutubeAccountId } } : {}),
        });
        const data = Array.isArray(response.data?.data) ? response.data.data : [];
        data.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
        setRows(data);
      } catch {
        const response = await api.get<{ data: Array<{ id: number; name: string }> }>("/app/lives/playlists");
        const data = Array.isArray(response.data?.data) ? response.data.data : [];
        const normalized: LivePlaylistAdmin[] = data.map((p) => ({
          id: Number(p.id),
          name: String((p as any).name ?? p.id),
          youtubeId: "",
        }));
        normalized.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
        setRows(normalized);
      }
    } finally {
      setLoading(false);
    }
  }, [isOwner, filterYoutubeAccountId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (isOwner) return;
    let canceled = false;
    const loadYoutubeAccounts = async () => {
      try {
        const response = await api.get<{ data: YoutubeAccountOption[] }>("/app/lives/youtube-accounts");
        const data = Array.isArray(response.data?.data) ? response.data.data : [];
        const normalized = data
          .map((a) => ({ id: Number(a.id), name: String(a.name ?? a.id) }))
          .filter((a) => Number.isFinite(a.id) && !!a.name);
        normalized.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
        if (!canceled) setYoutubeAccounts(normalized);
      } catch {
        if (!canceled) setYoutubeAccounts([]);
      }
    };
    void loadYoutubeAccounts();
    return () => {
      canceled = true;
    };
  }, [isOwner]);

  useEffect(() => {
    let canceled = false;
    const loadEstablishments = async () => {
      setLoadingEstablishments(true);
      try {
        const options = await fetchEstablishmentsForFilters({ country });
        const normalized: EstablishmentOption[] = options.map((option) => ({
          id: option.id,
          name: option.name,
          livePlaylistId: option.livePlaylistId,
        }));
        normalized.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
        if (!canceled) setEstablishments(normalized);
      } catch {
        if (!canceled) setEstablishments([]);
      } finally {
        if (!canceled) setLoadingEstablishments(false);
      }
    };
    void loadEstablishments();
    return () => {
      canceled = true;
    };
  }, [country]);

  const ownerEstablishmentId = useMemo(() => {
    if (!isOwner) return null;
    return establishments.length === 1 ? establishments[0].id : null;
  }, [isOwner, establishments]);

  const selectedEstablishment = useMemo(
    () => establishments.find((e) => e.id === draftEstablishmentId) ?? null,
    [draftEstablishmentId, establishments]
  );

  const openCreate = () => {
    setDraft({ name: "", youtubeId: "", youtubeAccountId: "" });
    setDraftEstablishmentId(isOwner && ownerEstablishmentId ? ownerEstablishmentId : "");
    setNameTouched(false);
    setDialogOpen(true);
  };

  const openEdit = (row: LivePlaylistAdmin) => {
    setDraft({ id: row.id, name: row.name, youtubeId: row.youtubeId, youtubeAccountId: "" });
    const linked = establishments.find((e) => e.livePlaylistId === row.id);
    setDraftEstablishmentId(linked?.id ?? (isOwner && ownerEstablishmentId ? ownerEstablishmentId : ""));
    setNameTouched(false);
    setDialogOpen(true);
  };

  const closeDialog = () => setDialogOpen(false);

  useEffect(() => {
    if (!dialogOpen) return;
    if (draftEstablishmentId) return;
    if (!draft.id) return;
    const linked = establishments.find((e) => e.livePlaylistId === draft.id);
    if (linked?.id) setDraftEstablishmentId(linked.id);
  }, [dialogOpen, draft.id, draftEstablishmentId, establishments]);

  useEffect(() => {
    if (!dialogOpen) return;
    if (!selectedEstablishment) return;
    if (nameTouched) return;
    if (draft.id) return; // only auto-fill on create; edit stays as-is unless admin changes manually
    const autoName = `TRANSMISSÕES - ${selectedEstablishment.name}`;
    if (draft.name !== autoName) setDraft((d) => ({ ...d, name: autoName }));
  }, [dialogOpen, draft.id, draft.name, nameTouched, selectedEstablishment]);

  const save = async () => {
    if (!draft.name.trim()) return;
    const payload: any = { name: draft.name.trim() };
    if (draft.youtubeId.trim()) payload.youtubeId = draft.youtubeId.trim();
    if (draftEstablishmentId) payload.establishmentId = draftEstablishmentId;
    if (!draft.id && draft.youtubeAccountId !== "") {
      payload.youtubeAccountId = draft.youtubeAccountId;
    }
    try {
      if (draft.id) {
        await api.put(`/app/lives/playlists/${draft.id}`, payload);
      } else {
        await api.post(`/app/lives/playlists`, payload);
      }
      setDialogOpen(false);
      await load();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || intl.formatMessage({ id: "global.generic_error" }));
    }
  };

  const del = async (id: number) => {
    try {
      await api.delete(`/app/lives/playlists/${id}`);
      await load();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || intl.formatMessage({ id: "global.generic_error" }));
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    const target = deleteTarget;
    setDeleteTarget(null);
    await del(target.id);
  };

  return (
    <Card>
      <CardContent>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ sm: "center" }}
          justifyContent="space-between"
          spacing={2}
          sx={{ mb: 2 }}
        >
          <Typography variant="h6">{intl.formatMessage({ id: "livePlaylists.title" })}</Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
            {!isOwner ? (
              <Autocomplete
                options={youtubeAccounts}
                filterOptions={youtubeAccountFilter}
                value={youtubeAccounts.find((a) => a.id === filterYoutubeAccountId) ?? null}
                onChange={(_, next) => setFilterYoutubeAccountId(next?.id ?? "")}
                getOptionLabel={(opt) => opt.name}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                sx={{ minWidth: 220 }}
                label={intl.formatMessage({ id: "livePlaylists.filterYoutubeAccount" })}
                ListboxProps={{ style: { maxHeight: 280 } }}
              />
            ) : null}
            <Tooltip
              title={
                youtubeBlocked ? intl.formatMessage({ id: "youtubeConnection.blockingWarning" }) : ""
              }
            >
              <span>
                <LoadingButton
                  startIcon={<AddIcon />}
                  variant="contained"
                  size="small"
                  onClick={openCreate}
                  disabled={loading || youtubeBlocked}
                  fullWidth={false}
                >
                  {intl.formatMessage({ id: "livePlaylists.add" })}
                </LoadingButton>
              </span>
            </Tooltip>
          </Stack>
        </Stack>

        <TableContainer sx={{ maxHeight: "65vh" }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ minWidth: 90 }}>{intl.formatMessage({ id: "livePlaylists.id" })}</TableCell>
                <TableCell sx={{ minWidth: 150 }}>{intl.formatMessage({ id: "livePlaylists.name" })}</TableCell>
                <TableCell sx={{ minWidth: 150 }}>{intl.formatMessage({ id: "livePlaylists.youtubeId" })}</TableCell>
                <TableCell sx={{ minWidth: 180 }}>{intl.formatMessage({ id: "livePlaylists.youtubeAccountColumn" })}</TableCell>
                <TableCell sx={{ minWidth: 120 }} align="right">
                  {intl.formatMessage({ id: "livePlaylists.actions" })}
                </TableCell>
              </TableRow>
            </TableHead>
            <Fade in key={loading ? "loading" : "loaded"} timeout={200}>
              <TableBody>
                {loading && <TableSkeletonRows rows={5} columns={5} />}
                {!loading &&
                  rows.map((r) => (
                    <TableRow key={r.id} hover>
                      <TableCell>{r.id}</TableCell>
                      <TableCell>{r.name}</TableCell>
                      <TableCell sx={{ fontFamily: "monospace" }}>{r.youtubeId}</TableCell>
                      <TableCell>{r.youtubeAccountName || "-"}</TableCell>
                      <TableCell align="right">
                        <IconButton onClick={() => openEdit(r)} aria-label="edit">
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                        <IconButton onClick={() => setDeleteTarget(r)} aria-label="delete">
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                {!loading && !rows.length && (
                  <TableEmptyState colSpan={5}>
                    {intl.formatMessage({ id: "livePlaylists.empty" })}
                  </TableEmptyState>
                )}
              </TableBody>
            </Fade>
          </Table>
        </TableContainer>

        <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="sm">
          <DialogTitle>
            {draft.id
              ? intl.formatMessage({ id: "livePlaylists.editTitle" })
              : intl.formatMessage({ id: "livePlaylists.createTitle" })}
          </DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              {isOwner ? null : (
              <Autocomplete
                options={establishments}
                filterOptions={establishmentFilter}
                value={establishments.find((e) => e.id === draftEstablishmentId) ?? null}
                onChange={(_, next) => {
                  setDraftEstablishmentId(next?.id ?? "");
                  if (!draft.id) setNameTouched(false);
                }}
                loading={loadingEstablishments}
                getOptionLabel={(opt) => opt.name}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                label={intl.formatMessage({ id: "livePlaylists.establishment" })}
                renderInput={(params) => (
                  <Input
                    {...params}
                    InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingEstablishments ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                    sx={{ marginY: 0 }}
                  />
                )}
                ListboxProps={{ style: { maxHeight: 280 } }}
              />
              )}
              <Input
                label={intl.formatMessage({ id: "livePlaylists.name" })}
                value={draft.name}
                onChange={(e) => {
                  setNameTouched(true);
                  setDraft((d) => ({ ...d, name: e.target.value }));
                }}
              />
              <Input
                label={intl.formatMessage({ id: "livePlaylists.youtubeId" })}
                value={draft.youtubeId}
                onChange={(e) => setDraft((d) => ({ ...d, youtubeId: e.target.value }))}
                helperText={intl.formatMessage({ id: "livePlaylists.youtubeHint" })}
              />
              {!draft.id && !isOwner ? (
                <Autocomplete
                  options={youtubeAccounts}
                  filterOptions={youtubeAccountFilter}
                  value={youtubeAccounts.find((a) => a.id === draft.youtubeAccountId) ?? null}
                  onChange={(_, next) => setDraft((d) => ({ ...d, youtubeAccountId: next?.id ?? "" }))}
                  getOptionLabel={(opt) => opt.name}
                  isOptionEqualToValue={(a, b) => a.id === b.id}
                  label={intl.formatMessage({ id: "livePlaylists.youtubeAccountId" })}
                  helperText={intl.formatMessage({ id: "livePlaylists.youtubeAccountIdHint" })}
                  error={draft.youtubeAccountId === ""}
                  ListboxProps={{ style: { maxHeight: 280 } }}
                />
              ) : null}
            </Stack>
          </DialogContent>
          <DialogActions>
            <LoadingButton size="small" onClick={closeDialog} variant="text" fullWidth={false}>{intl.formatMessage({ id: "livePlaylists.cancel" })}</LoadingButton>
            <LoadingButton
              variant="contained"
              size="small"
              onClick={save}
              disabled={!draft.name.trim() || (!draft.id && !isOwner && draft.youtubeAccountId === "")}
              fullWidth={false}
            >
              {intl.formatMessage({ id: "livePlaylists.save" })}
            </LoadingButton>
          </DialogActions>
        </Dialog>

        <ConfirmDialog
          open={!!deleteTarget}
          title={intl.formatMessage({ id: "common.confirm" })}
          message={intl.formatMessage({ id: "deleteDialog.confirmMessage" })}
          confirmLabel={intl.formatMessage({ id: "common.yes" })}
          cancelLabel={intl.formatMessage({ id: "common.no" })}
          onConfirm={handleConfirmDelete}
          onClose={() => setDeleteTarget(null)}
        />
      </CardContent>
    </Card>
  );
}
